import api from './api';

// =============================================================================
// B4: module x level rights -- the shared contract (plan §1.3/§1.4)
// =============================================================================
//
// Mirrors backend/apps/brokers/rights_catalog.py. The server is the authority:
// `/employee-rights/me/` answers with `modules`, and every write is refused by
// `ModuleRight` on the API whatever the screen shows. What lives here is only
// (a) the vocabulary and (b) a FALLBACK matrix for a backend that has not
// deployed `modules` yet, derived from the legacy `rights` codes + BASELINE.

export const RIGHTS_MODULES = [
  'contacts',
  'policies',
  'quotes',
  'leads',
  'renewals',
  'claims',
  'servicing',
  'commissions',
  'reports',
  'channels',
  'reconciliation',
] as const;
export type RightsModule = (typeof RIGHTS_MODULES)[number];

export const RIGHTS_LEVELS = ['read', 'create', 'edit', 'delete'] as const;
export type RightsLevel = (typeof RIGHTS_LEVELS)[number];

export type ModuleMatrix = Record<RightsModule, Record<RightsLevel, boolean>>;
export type ModuleOverrides = Partial<Record<RightsModule, Partial<Record<RightsLevel, boolean | null>>>>;
/** Every module and every level present, unlike `ModuleOverrides` -- what `_role_default_cells` (brokers/views.py) returns: `null` means no default row for that cell. */
export type ModuleDefaultsMatrix = Record<RightsModule, Record<RightsLevel, boolean | null>>;
/**
 * Where a resolved cell's answer came from (`SOURCE_*` in
 * backend/apps/brokers/permissions.py).
 *
 * `admin` is the resolver's short-circuit: a subject whose LOGIN role is an
 * admin role holds every right before any row is read, so `effective` reports
 * the whole matrix true with this source and `unrestricted: true`. It is not
 * a row anyone can edit -- `bulk_set` refuses such a subject -- so the screen
 * says so rather than offering toggles that govern nothing.
 */
export type RightsSource =
  | 'override'
  | 'legacy'
  | 'role_default'
  | 'baseline'
  | 'locked'
  | 'admin'
  | 'none';
export type ModuleSources = Partial<Record<RightsModule, Partial<Record<RightsLevel, RightsSource>>>>;
export type ModuleLevelList = Partial<Record<RightsModule, RightsLevel[]>>;
export type RightsSubjectType = 'employee' | 'posp';

export interface MyRightsResponse {
  role: string | null;
  version?: 2;
  rights: Record<string, boolean>;
  modules?: ModuleMatrix;
}

/** `GET /brokers/employee-rights/modules/` */
export interface RightsModulesCatalog {
  modules: { code: RightsModule; label: string }[];
  levels: { code: RightsLevel; label: string }[];
  locked: Record<RightsSubjectType, ModuleLevelList>;
  baseline: Record<RightsSubjectType, ModuleLevelList>;
}

/** `GET /brokers/employee-rights/effective/?employee=<id>` or `?posp=<id>` */
export interface EffectiveRightsResponse {
  subject_type: RightsSubjectType;
  subject_id: string;
  modules: ModuleMatrix;
  sources: ModuleSources;
  /** The subject's login role is an admin role: every cell is true by role and
   * no row of theirs is ever consulted. `bulk_set` refuses to write them. */
  unrestricted?: boolean;
}

export const RIGHTS_MODULE_LABELS: Record<RightsModule, string> = {
  contacts: 'Contacts',
  policies: 'Policies',
  quotes: 'Quotes',
  leads: 'Leads',
  renewals: 'Renewals',
  claims: 'Claims',
  servicing: 'Servicing Requests',
  commissions: 'Commissions',
  reports: 'Reports',
  channels: 'Channels & POSPs',
  reconciliation: 'Reconciliation',
};

export const RIGHTS_LEVEL_LABELS: Record<RightsLevel, string> = {
  read: 'Read',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
};

export const ADMIN_ROLES: readonly string[] = ['super_admin', 'country_admin', 'broker_admin'];
export const RIGHTS_GATED_ROLES: readonly string[] = ['broker_employee', 'posp', 'super_posp'];
export const POSP_ROLES: readonly string[] = ['posp', 'super_posp'];

/** legacy code -> (module, the single level a `has_right(code)` call means) */
export const LEGACY_CODE_LEVEL: Record<string, [RightsModule, RightsLevel]> = {
  add_customer: ['contacts', 'create'],
  add_quote: ['quotes', 'create'],
  add_policy: ['policies', 'create'],
  add_commission: ['commissions', 'create'],
  view_reports: ['reports', 'read'],
  manage_posps: ['channels', 'create'],
  view_leads: ['leads', 'read'],
};

/** A granted=false row for these codes never revoked read (read was never governed by them). */
export const LEGACY_READ_UNGOVERNED: readonly string[] = [
  'add_customer',
  'add_quote',
  'add_policy',
  'add_commission',
  'manage_posps',
];

const ALL_LEVELS: RightsLevel[] = [...RIGHTS_LEVELS];

/** Levels held when no row exists -- what the role floors admitted before B4. */
export const RIGHTS_BASELINE: Record<string, ModuleLevelList> = {
  broker_employee: {
    contacts: ['read'],
    policies: ['read'],
    quotes: ['read'],
    leads: [],
    renewals: ALL_LEVELS,
    claims: ALL_LEVELS,
    servicing: ALL_LEVELS,
    commissions: ['read'],
    reports: [],
    channels: ['read'],
    reconciliation: [],
  },
  posp: {
    contacts: ['read'],
    policies: ['read'],
    quotes: ['read'],
    leads: ['read', 'create', 'edit'],
    // R1 (20 Sep): an agent must SEE the renewals of the policies they sold in
    // order to chase them. BASELINE, not a row, so no broker has to switch it
    // on. The queue itself is narrowed to their sold book server-side.
    renewals: ['read'],
    // 29 Sep: agents FOLLOW claims, they no longer raise them (mirrors backend rights_catalog).
    claims: ['read'],
    servicing: ALL_LEVELS,
    commissions: ['read'],
    reports: [],
    channels: ['read'],
    reconciliation: [],
  },
};
RIGHTS_BASELINE.super_posp = RIGHTS_BASELINE.posp;

/** Cells a POSP can never hold (the resolver forces them false). */
export const POSP_LOCKED: ModuleLevelList = {
  policies: ['create', 'edit', 'delete'],
  contacts: ['delete'],
  leads: ['delete'],
  claims: ['create', 'edit', 'delete'],
  channels: ['create', 'edit', 'delete'],
  commissions: ['create', 'edit', 'delete'],
  // Read is NOT locked any more (R1, 20 Sep). This mirror is applied to the
  // server's own matrix in `useModuleMatrix`, so leaving `renewals` fully
  // locked here would have forced `useCan('renewals','read')` false for every
  // agent and hidden the tab the server had just opened -- a denied screen
  // with a permission fix behind it. Keep this list identical to
  // `POSP_LOCKED` in backend/apps/brokers/rights_catalog.py.
  renewals: ['create', 'edit', 'delete'],
  reconciliation: ['read', 'create', 'edit', 'delete'],
  reports: ALL_LEVELS,
};

/** A full matrix with every cell set to `value`. */
export function uniformMatrix(value: boolean): ModuleMatrix {
  const matrix = {} as ModuleMatrix;
  for (const module of RIGHTS_MODULES) {
    matrix[module] = { read: value, create: value, edit: value, delete: value };
  }
  return matrix;
}

/**
 * Coerce a server matrix into a complete one: a module or level the payload
 * omits is `false` (fail closed), never `undefined`.
 */
export function normalizeMatrix(modules: Partial<Record<string, Partial<Record<string, boolean>>>>): ModuleMatrix {
  const matrix = uniformMatrix(false);
  for (const module of RIGHTS_MODULES) {
    const row = modules[module];
    if (!row) continue;
    for (const level of RIGHTS_LEVELS) {
      matrix[module][level] = row[level] === true;
    }
  }
  return matrix;
}

/** Force every POSP_LOCKED cell of `matrix` to false (mutates and returns it). */
export function applyPospLocks(matrix: ModuleMatrix): ModuleMatrix {
  for (const [module, levels] of Object.entries(POSP_LOCKED) as [RightsModule, RightsLevel[]][]) {
    for (const level of levels) matrix[module][level] = false;
  }
  return matrix;
}

/**
 * The fallback for a `me` payload with no `modules` key (a backend older than
 * B4): start from BASELINE, then apply the legacy codes the way the backend's
 * resolver expands them.
 *
 * One deliberate difference from the server's row expansion: `me.rights` lists
 * every code with `false` for "no row", so a `false` cannot be told apart from
 * "never configured". A `false` therefore only removes what that code has
 * always governed -- create/edit/delete for the `add_*`/`manage_posps` codes --
 * and leaves `view_leads`/`view_reports` at BASELINE, which is empty for an
 * employee (so they stay denied, as before) and never applied to a POSP's
 * leads (the old gate never asked a POSP for `view_leads`).
 */
export function deriveMatrixFromLegacy(role: string, rights: Record<string, boolean> | undefined): ModuleMatrix {
  const matrix = uniformMatrix(false);
  const baseline = RIGHTS_BASELINE[role] ?? {};
  for (const [module, levels] of Object.entries(baseline) as [RightsModule, RightsLevel[]][]) {
    for (const level of levels) matrix[module][level] = true;
  }
  for (const [code, granted] of Object.entries(rights ?? {})) {
    const mapping = LEGACY_CODE_LEVEL[code];
    if (!mapping) continue;
    const [module] = mapping;
    if (granted === true) {
      for (const level of RIGHTS_LEVELS) matrix[module][level] = true;
    } else if (LEGACY_READ_UNGOVERNED.includes(code)) {
      matrix[module].create = false;
      matrix[module].edit = false;
      matrix[module].delete = false;
    }
  }
  if (POSP_ROLES.includes(role)) applyPospLocks(matrix);
  return matrix;
}

/**
 * Window event the axios interceptor fires on any 403; `useMyRights` refetches
 * on it. Lives here (not in api.ts) so the hook does not depend on the HTTP
 * module -- tests that stub `@/services/api` must not break the rights hook.
 */
export const RIGHTS_STALE_EVENT = 'policy:rights-stale';

/** Error code a `ModuleRight` refusal carries in `error.code`. */
export const MODULE_RIGHT_DENIED_CODE = 'module_right_denied';

const DENIAL_PATTERN = /You do not have (\w+) access to (\w+)\.?/;

/** `ADMIN_ONLY` in apps/brokers/permissions.py. */
const ADMIN_ONLY_LEVEL = 'admin_only';

/**
 * The backend words a refusal with codes ("You do not have create access to
 * servicing."). Say it with the labels a person sees on the Rights screen:
 * "You do not have Create access to Servicing Requests." Anything that does not
 * match the pattern comes back unchanged.
 */
export function describeModuleRightDenial(message: string | undefined | null): string {
  const fallback = 'You do not have access to do this.';
  if (!message) return fallback;
  const match = DENIAL_PATTERN.exec(message);
  if (!match) return message;
  const module = (RIGHTS_MODULE_LABELS as Record<string, string>)[match[2]] ?? match[2];
  // `admin_only` is the server's sentinel for money actions no rights row can
  // open (commission approve/reverse, payouts, customer logins, ...). It is
  // not a level a person can be granted, so never show the code.
  if (match[1] === ADMIN_ONLY_LEVEL) return `Only an administrator can do this in ${module}.`;
  const level = (RIGHTS_LEVEL_LABELS as Record<string, string>)[match[1]];
  if (!level) return `You do not have access to ${module}.`;
  return `You do not have ${level} access to ${module}.`;
}

/**
 * The 400 body a locked-cell bulk-set write gets back: `_RightsWriteSerializer.validate_modules`
 * raises `{module: {level: "A POSP cannot hold this right."}}`, which DRF
 * returns unwrapped as `{"modules": {...}}` -- or, for the legacy `rights`
 * shape, `{"rights": {code: "..."}}` (`POSPRightViewSet.bulk_set`). Neither
 * has a top-level `error`/`detail`/`message`/`errors` key, so
 * `getErrorMessage` (services/api.ts) falls through every branch it knows and
 * shows axios's generic "Request failed with status code 400" instead of the
 * server's own message.
 *
 * `RightsManagementPage` already disables a locked cell from `GET .../modules/`,
 * so this 400 should not be reachable through the screen in the ordinary case
 * -- but a stale catalog fetched before a lock changed, or any other direct
 * caller of `bulkSet`/`bulkSetModules`, can still hit it, and a caller should
 * see the actual reason, not a generic HTTP status line.
 *
 * Returns `null` for any other shape, so a caller can fall back to
 * `getErrorMessage(error)`.
 */
export function describeBulkSetError(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const modules = (data as Record<string, unknown>).modules;
  if (modules && typeof modules === 'object' && !Array.isArray(modules)) {
    for (const [module, levels] of Object.entries(modules as Record<string, unknown>)) {
      if (!levels || typeof levels !== 'object' || Array.isArray(levels)) continue;
      for (const message of Object.values(levels as Record<string, unknown>)) {
        if (typeof message === 'string' && message) {
          const label = (RIGHTS_MODULE_LABELS as Record<string, string>)[module] ?? module;
          return `${message} (${label})`;
        }
      }
    }
  }
  const rights = (data as Record<string, unknown>).rights;
  if (rights && typeof rights === 'object' && !Array.isArray(rights)) {
    for (const message of Object.values(rights as Record<string, unknown>)) {
      if (typeof message === 'string' && message) return message;
    }
  }
  return null;
}

/**
 * Re-throw a bulk-set failure wearing the SERVER's own words.
 *
 * `describeBulkSetError` existed but nothing called it, so the message it can
 * read never reached a screen: `useBulkSetModuleRights`'s `onError` toasts
 * `getErrorMessage(error)`, which finds no top-level `error`/`detail`/
 * `message`/`errors` key in `{"modules": {...}}` and falls through to axios's
 * "Request failed with status code 400". Setting `error.message` here is the
 * last branch `getErrorMessage` tries, so every existing caller -- the toast
 * included -- shows the real reason with no change of its own. Same technique
 * as `handleForbidden` in services/api.ts.
 */
function rethrowWithBulkSetMessage(error: unknown): never {
  const data = (error as { response?: { data?: unknown } } | null | undefined)?.response?.data;
  const message = describeBulkSetError(data);
  if (message && error && typeof error === 'object') {
    try {
      (error as { message?: string }).message = message;
    } catch {
      // A frozen error object: rethrow it unchanged rather than failing here.
    }
  }
  throw error;
}

export interface EmployeeRight {
  id: string;
  broker: string;
  employee: string;
  employee_name?: string;
  /** Legacy rows carry a code; module rows (B4) carry module + level instead. */
  right_code: string | null;
  right_label: string | null;
  module?: RightsModule | null;
  module_label?: string | null;
  level?: RightsLevel | null;
  level_label?: string | null;
  granted: boolean;
  granted_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface RightCatalogEntry {
  code: string;
  label: string;
}

export interface BulkSetRightsResponse {
  employee_id: string;
  rights: EmployeeRight[];
  /**
   * `EmployeeRightViewSet.bulk_set` always includes these, legacy `rights`
   * shape or `modules` shape alike (it resolves and returns the subject's
   * full matrix either way). Optional here only for a body that failed
   * validation before that point (see `describeBulkSetError`).
   */
  modules?: ModuleMatrix;
  sources?: ModuleSources;
}

export interface RoleRightDefault {
  id: string;
  broker: string;
  role: string;
  role_label: string;
  /** Legacy rows carry a code; module rows (B4) carry module + level instead. */
  right_code: string | null;
  right_label: string | null;
  module?: RightsModule | null;
  module_label?: string | null;
  level?: RightsLevel | null;
  level_label?: string | null;
  granted: boolean;
  updated_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface BulkSetRoleDefaultsResponse {
  broker_id: string;
  role: string;
  /** `role_defaults_bulk_set` always includes this (brokers/views.py); `null` per cell means no default row. */
  modules?: ModuleDefaultsMatrix;
  rights: RoleRightDefault[];
}



/** A single row from the combined Rights Management listing (point 11). */
export type RightsUserType = 'employee' | 'posp' | 'advertiser' | 'consolidator';

export interface RightsUser {
  id: string;
  user_type: RightsUserType;
  name: string;
  email: string;
  phone: string;
  is_active: boolean;
}

export const employeeRightsService = {
  async list(employeeId?: string): Promise<EmployeeRight[]> {
    const params = employeeId ? { employee: employeeId } : undefined;
    const response = await api.get<EmployeeRight[]>('/brokers/employee-rights/', { params });
    return response.data;
  },

  async getCatalog(): Promise<RightCatalogEntry[]> {
    const response = await api.get<RightCatalogEntry[]>('/brokers/employee-rights/catalog/');
    return response.data;
  },

  async bulkSet(employeeId: string, rights: Record<string, boolean>): Promise<BulkSetRightsResponse> {
    try {
      const response = await api.post<BulkSetRightsResponse>('/brokers/employee-rights/bulk-set/', {
        employee_id: employeeId,
        rights,
      });
      return response.data;
    } catch (error) {
      rethrowWithBulkSetMessage(error);
    }
  },

  /** Department/role catalog (BrokerEmployee.ROLE_CHOICES) used to key role defaults. */
  async getRoles(): Promise<RightCatalogEntry[]> {
    const response = await api.get<RightCatalogEntry[]>('/brokers/employee-rights/roles/');
    return response.data;
  },

  async getRoleDefaults(role?: string): Promise<RoleRightDefault[]> {
    const params = role ? { role } : undefined;
    const response = await api.get<RoleRightDefault[]>('/brokers/employee-rights/role-defaults/', { params });
    return response.data;
  },

  async bulkSetRoleDefaults(role: string, rights: Record<string, boolean>): Promise<BulkSetRoleDefaultsResponse> {
    const response = await api.post<BulkSetRoleDefaultsResponse>(
      '/brokers/employee-rights/role-defaults/bulk-set/',
      { role, rights }
    );
    return response.data;
  },

  /** The CALLER's own effective rights (role default + per-user override) -- the self-gating source. */
  async getMyRights(): Promise<MyRightsResponse> {
    const response = await api.get<MyRightsResponse>('/brokers/employee-rights/me/');
    return response.data;
  },

  /** B4: the module/level vocabulary, locked cells and baseline (broker admin). */
  async getModulesCatalog(): Promise<RightsModulesCatalog> {
    const response = await api.get<RightsModulesCatalog>('/brokers/employee-rights/modules/');
    return response.data;
  },

  /** B4: one subject's resolved 10x4 matrix and where each cell came from. */
  async getEffective(subjectType: RightsSubjectType, subjectId: string): Promise<EffectiveRightsResponse> {
    const response = await api.get<EffectiveRightsResponse>('/brokers/employee-rights/effective/', {
      params: { [subjectType]: subjectId },
    });
    return response.data;
  },

  /**
   * B4: write module/level overrides. `true`/`false` upserts an override,
   * `null` removes it so the cell falls back to the role default or baseline.
   */
  async bulkSetModules(employeeId: string, modules: ModuleOverrides): Promise<BulkSetRightsResponse> {
    try {
      const response = await api.post<BulkSetRightsResponse>('/brokers/employee-rights/bulk-set/', {
        employee_id: employeeId,
        modules,
      });
      return response.data;
    } catch (error) {
      rethrowWithBulkSetMessage(error);
    }
  },

  /**
   * Combined Rights Management listing (point 11): every POSP, Advertiser,
   * Employee and Consolidator user under the caller's own broker. Scoped
   * server-side -- never returns another tenant's users.
   */
  async getRightsUsers(): Promise<RightsUser[]> {
    const response = await api.get<RightsUser[]>('/brokers/employee-rights/users/');
    return response.data;
  },
};

/**
 * B6: POSP rights. Same catalog and same contract as employee rights, backed by
 * the POSPRight model -- previously POSPs were listed on the Rights Management
 * screen but filtered out of the editable set, because a POSP grant had nowhere
 * to persist.
 *
 * The catalog is deliberately NOT duplicated: both tabs read
 * `/employee-rights/catalog/`, so the two can never offer different codes.
 */
export interface POSPRight {
  id: string;
  broker: string;
  posp: string;
  posp_name?: string;
  right_code: string | null;
  right_label: string | null;
  module?: RightsModule | null;
  module_label?: string | null;
  level?: RightsLevel | null;
  level_label?: string | null;
  granted: boolean;
  granted_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface BulkSetPOSPRightsResponse {
  posp_id: string;
  rights: POSPRight[];
  /** `POSPRightViewSet.bulk_set` always includes these; see the note on `BulkSetRightsResponse`. */
  modules?: ModuleMatrix;
  sources?: ModuleSources;
}

export const pospRightsService = {
  async list(pospId?: string): Promise<POSPRight[]> {
    const params = pospId ? { posp: pospId } : undefined;
    const response = await api.get<POSPRight[]>('/brokers/posp-rights/', { params });
    return response.data;
  },

  async bulkSet(pospId: string, rights: Record<string, boolean>): Promise<BulkSetPOSPRightsResponse> {
    try {
      const response = await api.post<BulkSetPOSPRightsResponse>('/brokers/posp-rights/bulk-set/', {
        posp_id: pospId,
        rights,
      });
      return response.data;
    } catch (error) {
      rethrowWithBulkSetMessage(error);
    }
  },

  /** B4: same contract as `employeeRightsService.bulkSetModules`, keyed by POSP. */
  async bulkSetModules(pospId: string, modules: ModuleOverrides): Promise<BulkSetPOSPRightsResponse> {
    try {
      const response = await api.post<BulkSetPOSPRightsResponse>('/brokers/posp-rights/bulk-set/', {
        posp_id: pospId,
        modules,
      });
      return response.data;
    } catch (error) {
      rethrowWithBulkSetMessage(error);
    }
  },
};
